/**
 * <main-cart> — cart page line-item updates via Section Rendering API.
 *
 * Loaded ONLY from `sections/main-cart_items.liquid`.
 */

import { Delegate } from '@theme/delegate';
import { buildCartQuantityUpdate } from '@theme/cart-api';
import { getVisibleCartCount, renderHeaderCartCount } from '@theme/cart-visible-count';

const CART_PAGE_USP_TEMPLATE_ID = 'cart-page-usp-template';
const SECTION_SYNC_ATTEMPTS = 3;
const SECTION_SYNC_DELAY_MS = 150;

function uspHasItems(uspEl) {
  return !!uspEl?.querySelector(
    '.ann-bar__block-item, .cart-drawer__usp-bar__item, .cart-footer__usp-single'
  );
}

function cloneUspForCache(uspEl) {
  const clone = uspEl.cloneNode(true);
  clone.querySelectorAll('[data-usp-clone="true"]').forEach((node) => node.remove());
  clone.querySelectorAll('[data-usp-clone-set="true"]').forEach((node) => node.remove());

  const uspSlider = clone.querySelector('cart-usps-slider');
  if (uspSlider) {
    const animWrapper = uspSlider.querySelector('.pm__usps-slider--anim-wrapper');
    const duplicate = uspSlider.querySelector('.pm__usps-slider--duplicate-wrapper');

    if (animWrapper) {
      while (animWrapper.children.length > 1) {
        animWrapper.removeChild(animWrapper.lastChild);
      }
      animWrapper.style.cssText = '';
    }

    if (duplicate) {
      duplicate.style.cssText = '';
    }

    uspSlider.style.height = '';
    uspSlider.removeAttribute('data-usp-layout');
    uspSlider.removeAttribute('data-usp-count');
  }

  return clone;
}

class MainCart extends HTMLElement {
  #delegateElement = null;
  #uspCache = null;
  #isUpdating = false;

  connectedCallback() {
    this.#bindDelegatedEvents();
    this.#restoreUspMarkup();
    this.#updateUspCache();
    this.#notifyUspRender();
    this.addEventListener('product:added', this.#onProductAdded);
  }

  disconnectedCallback() {
    this.removeEventListener('product:added', this.#onProductAdded);
    this.#delegateElement?.off?.();
    this.#delegateElement = null;
    this.#uspCache = null;
  }

  #bindDelegatedEvents() {
    this.#delegateElement?.off?.();
    this.#delegateElement = new Delegate(this);

    this.#delegateElement.on(
      'click',
      '[data-action="update-item-quantity"], [data-action="remove-item"]',
      this.#updateItemQuantity
    );

    this.#delegateElement.on(
      'change',
      '[data-action="update-change-quantity"]',
      this.#updateItemQuantity
    );

    this.#delegateElement.on(
      'quantity:changed',
      '.QuantitySelector__CurrentQuantity',
      this.#updateItemQuantity
    );
  }

  async #getCartProductIds() {
    const cart = await this.#fetchCartJson();
    if (!cart?.items?.length) return [];
    return [...new Set(cart.items.map((item) => item.product_id))];
  }

  async #refreshRecommendations() {
    const cart = await this.#fetchCartJson();
    const upsells = [...this.querySelectorAll('cart-upsell-full')];

    if (!cart?.items?.length) {
      upsells.forEach((upsell) => upsell.remove());
      return;
    }

    const productIds = [...new Set(cart.items.map((item) => item.product_id))];
    await Promise.all(upsells.map((upsell) => upsell.refresh?.(productIds)));
  }

  #seedUspCacheFromTemplate() {
    const template = this.querySelector(`#${CART_PAGE_USP_TEMPLATE_ID}`);
    if (!(template instanceof HTMLTemplateElement)) return;

    const usp = template.content.querySelector('.cart-drawer__usp');
    if (uspHasItems(usp)) {
      this.#uspCache = cloneUspForCache(usp);
    }
  }

  #getUspMarkupSource() {
    this.#seedUspCacheFromTemplate();
    if (this.#uspCache && uspHasItems(this.#uspCache)) {
      return this.#uspCache;
    }
    return null;
  }

  #updateUspCache() {
    const mounts = [...this.querySelectorAll('.cart-drawer__usp-mount')].filter((mount) =>
      this.#isMountVisible(mount)
    );
    const mount = mounts[0];
    const usp = mount?.querySelector('.cart-drawer__usp');
    if (uspHasItems(usp)) {
      this.#uspCache = cloneUspForCache(usp);
    }
  }

  #isMountVisible(mount) {
    return mount.checkVisibility?.({ checkVisibilityCSS: true }) ?? mount.getClientRects().length > 0;
  }

  #getShippingZoneKey(zone) {
    const slot = zone.closest('.main-cart__shipping-zone-slot');
    if (slot?.classList.contains('not_desktop')) return 'mobile';
    return 'desktop';
  }

  #extractShippingZones(root) {
    const preserved = new Map();
    if (!root) return preserved;

    root.querySelectorAll('.cart-drawer__shipping-zone').forEach((zone) => {
      const mount = zone.querySelector('.cart-drawer__usp-mount');
      const usp = mount?.querySelector('.cart-drawer__usp');
      if (!uspHasItems(usp)) return;

      preserved.set(this.#getShippingZoneKey(zone), zone);
      zone.remove();
    });

    return preserved;
  }

  #graftShippingZones(preserved) {
    if (!preserved?.size) return;

    this.querySelectorAll('.cart-drawer__shipping-zone').forEach((zone) => {
      const mount = zone.querySelector('.cart-drawer__usp-mount');
      const usp = mount?.querySelector('.cart-drawer__usp');
      if (uspHasItems(usp)) return;

      const preservedZone = preserved.get(this.#getShippingZoneKey(zone));
      if (preservedZone) {
        zone.replaceWith(preservedZone);
      }
    });
  }

  #restoreUspMarkup() {
    const source = this.#getUspMarkupSource();
    if (!source) return;

    this.querySelectorAll('.cart-drawer__usp-mount').forEach((mount) => {
      if (!this.#isMountVisible(mount)) return;

      const existingUsp = mount.querySelector('.cart-drawer__usp');
      if (uspHasItems(existingUsp)) return;

      mount.replaceChildren(source.cloneNode(true));

      const slider = mount.querySelector('cart-usps-slider');
      if (typeof slider?.reinitialize === 'function') {
        requestAnimationFrame(() => {
          slider.reinitialize();
        });
      }
    });
  }

  #notifyUspRender() {
    requestAnimationFrame(() => {
      this.dispatchEvent(new CustomEvent('cart:render', { bubbles: true }));
    });
  }

  #syncHeaderCartBadge(cartData, { notify = true } = {}) {
    const visibleCount = cartData ? getVisibleCartCount(cartData) : 0;
    window.theme = window.theme || {};
    window.theme.cartCount = visibleCount;
    renderHeaderCartCount(visibleCount);

    if (!notify) return;

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          item_count: cartData?.item_count ?? 0,
          visible_count: visibleCount,
          product_ids: cartData
            ? [...new Set((cartData.items || []).map((item) => item.product_id))]
            : null,
        },
      })
    );
  }

  #onProductAdded = async (event) => {
    if (window.location.pathname !== window.routes.cart_page_url) return;

    const card = event.target?.closest?.('product-card');
    if (!card || !this.contains(card)) return;

    const slide = card.closest('.swiper-slide[data-product-id]');
    const upsell = slide?.closest('cart-upsell-full');

    if (this.#isUpdating) return;

    try {
      await this.#renderCart({ skipPageLoader: true, deferRecommendationRefresh: true });
      await this.#scrollToTop();
    } finally {
      card.finishMainCartRecommendationFlow?.();
      upsell?.removeSlide?.(slide);
      await this.#refreshRecommendations();
    }
  };

  #scrollToTop() {
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const target = this.querySelector('.main-cart__header') ?? this;
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight);

    if (Math.abs(window.scrollY - top) <= 2) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const maxWaitMs = 2000;
      const maxWaitTimer = setTimeout(resolve, maxWaitMs);

      const finish = () => {
        clearTimeout(maxWaitTimer);
        resolve();
      };

      window.addEventListener('scrollend', finish, { once: true, passive: true });
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  #getSectionItemCount(root) {
    return root.querySelectorAll('.main-cart__line-items .cart-item, .cart-drawer__items .cart-item').length;
  }

  async #fetchCartJson() {
    const response = await fetch(`${window.routes.localeRootUrl}/cart.js?t=${Date.now()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      cache: 'no-store',
    });

    if (!response.ok) return null;
    return response.json();
  }

  async #fetchSectionHtml() {
    const response = await fetch(
      `${window.routes.cart_page_url}?section_id=main-cart_items&t=${Date.now()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    return response.text();
  }

  async #fetchSyncedSectionHtml(cartData) {
    let cartCount = cartData?.item_count ?? 0;
    let html = await this.#fetchSectionHtml();

    for (let attempt = 0; attempt < SECTION_SYNC_ATTEMPTS; attempt += 1) {
      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;
      const sectionCount = this.#getSectionItemCount(tempElement);

      if (sectionCount === cartCount) {
        return html;
      }

      if (sectionCount > cartCount) {
        const cart = await this.#fetchCartJson();
        if (cart) {
          cartData = cart;
          cartCount = cart.item_count ?? 0;
        }
      }

      if (attempt < SECTION_SYNC_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, SECTION_SYNC_DELAY_MS));
        html = await this.#fetchSectionHtml();
      }
    }

    return html;
  }

  #updateItemQuantity = async (event, target) => {
    event.preventDefault();
    if (this.#isUpdating) return;

    const cartItem = target.closest('.cart-item');
    cartItem?.querySelector('.item-loader')?.classList.add('is-visible');

    let quantity;
    if (target.tagName === 'INPUT') {
      quantity = Math.max(parseInt(target.value, 10) || 0, 0);
    } else {
      quantity = parseInt(target.getAttribute('data-quantity'), 10);
    }

    const lineId = target.getAttribute('data-line-id');
    if (!lineId) {
      cartItem?.querySelector('.item-loader')?.classList.remove('is-visible');
      return;
    }

    this.#isUpdating = true;

    try {
      const cartRes = await fetch(`${window.routes.localeRootUrl}/cart.js?t=${Date.now()}`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });
      const cart = cartRes.ok ? await cartRes.json() : null;
      const { endpoint, body } = buildCartQuantityUpdate(cart, lineId, quantity);

      await fetch(`${window.routes.localeRootUrl}${endpoint}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      await this.#renderCart();
    } catch (err) {
      console.error('Failed to update item quantity:', err);
      cartItem?.querySelector('.item-loader')?.classList.remove('is-visible');
    } finally {
      this.#isUpdating = false;
    }
  };

  async #renderCart({ skipPageLoader = false, deferRecommendationRefresh = false } = {}) {
    const pageLoader = document.body.querySelector('.page-loader');
    if (!skipPageLoader && pageLoader) pageLoader.style.width = '100%';

    try {
      const cartData = await this.#fetchCartJson();
      const html = await this.#fetchSyncedSectionHtml(cartData);
      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;

      const newWrapper = tempElement.querySelector('.main-cart__wrapper');
      const currentWrapper = this.querySelector('.main-cart__wrapper');
      const preservedShippingZones = currentWrapper
        ? this.#extractShippingZones(currentWrapper)
        : new Map();

      if (newWrapper && currentWrapper) {
        currentWrapper.replaceWith(newWrapper);
      }

      this.#graftShippingZones(preservedShippingZones);

      this.#bindDelegatedEvents();
      if (!deferRecommendationRefresh) {
        await this.#refreshRecommendations();
      }
      this.#syncHeaderCartBadge(cartData, { notify: preservedShippingZones.size === 0 });

      if (preservedShippingZones.size === 0) {
        this.#restoreUspMarkup();
        this.#updateUspCache();
        this.#notifyUspRender();
      }
    } catch (err) {
      console.error('Failed to render cart:', err);
    } finally {
      if (!skipPageLoader && pageLoader) {
        pageLoader.style.width = '0%';
      }
    }
  }
}

if (!customElements.get('main-cart')) {
  customElements.define('main-cart', MainCart);
}
